import {
  getActiveAuthService,
  isLoopbackIssuer,
  issuerFromRequest,
} from "@brains/auth-service";
import {
  JwksResolver,
  signRequest,
  verifyRequest,
} from "@brains/http-signatures";
import {
  InterfacePlugin,
  type InterfacePluginContext,
  type Tool,
  type WebRouteDefinition,
} from "@brains/plugins";
import type { Daemon, AgentNamespace } from "@brains/plugins";
import type { UserPermissionLevel } from "@brains/templates";
import type { AgentCard } from "@a2a-js/sdk";
import { Hono } from "hono";
import { a2aConfigSchema, type A2AConfig, type A2AConfigInput } from "./config";
import { buildAgentCard } from "./agent-card";
import { buildAgentDirectory } from "./agent-directory";
import { TaskManager } from "./task-manager";
import { A2ATurnSupervisor } from "./turn-supervisor";
import {
  handleJsonRpc,
  handleStreamMessage,
  jsonrpcRequestSchema,
  streamParamsSchema,
} from "./jsonrpc-handler";
import {
  createAgentCallTool,
  type A2AClientDeps,
  type A2ARequestSigner,
  type FetchFn,
} from "./client";
import { registerA2ACallMessageHandlers } from "./message-handlers";
import {
  RuntimePublicAskAllowance,
  type PublicAskAllowance,
} from "./public-asks";
import packageJson from "../package.json";

const A2A_CORS_HEADERS = {
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Methods": "GET, POST, OPTIONS",
  "Access-Control-Allow-Headers":
    "Content-Type, Signature, Signature-Input, Content-Digest, Date",
  "X-Content-Type-Options": "nosniff",
} as const;

/**
 * A2A Interface Plugin
 *
 * Implements the Agent-to-Agent protocol for inter-brain communication.
 * Serves an Agent Card for discovery and accepts tasks via JSON-RPC 2.0.
 * Routes tasks through AgentService (conversational, like Matrix/Discord).
 */
/**
 * Runtime collaborators the interface needs that are not configuration.
 *
 * Config is schema-validated operator input; a fetch implementation is not.
 * Threading it here means a test can hand the outbound client a fake and read
 * the requests off it, instead of reassigning globalThis.fetch and restoring
 * it afterwards. Production leaves it unset and the client uses the global.
 */
export interface A2AInterfaceDeps {
  fetch?: FetchFn | undefined;
}

export class A2AInterface extends InterfacePlugin<A2AConfig, A2AConfigInput> {
  declare protected config: A2AConfig;
  private readonly deps: A2AInterfaceDeps;
  private cardReady = false;
  private taskManager = new TaskManager();
  private readonly turnSupervisor = new A2ATurnSupervisor();
  private agentService: AgentNamespace | undefined;
  private readonly jwksResolver: JwksResolver;
  private app: Hono | undefined;
  private hasWebserver = false;

  constructor(config: A2AConfigInput = {}, deps: A2AInterfaceDeps = {}) {
    if (Object.prototype.hasOwnProperty.call(config, "trustedTokens")) {
      throw new Error("trustedTokens legacy config is no longer supported");
    }
    if (Object.prototype.hasOwnProperty.call(config, "outboundTokens")) {
      throw new Error("outboundTokens legacy config is no longer supported");
    }
    super("a2a", packageJson, config, a2aConfigSchema);
    this.deps = deps;
    // Inbound signature verification fetches the peer's JWKS through the same
    // fetch the outbound client uses, so one injected fake covers both.
    this.jwksResolver = new JwksResolver(
      deps.fetch ? { fetch: deps.fetch } : {},
    );
  }

  protected override async onRegister(
    context: InterfacePluginContext,
  ): Promise<void> {
    await super.onRegister(context);

    this.hasWebserver = context.plugins.has("webserver");
    this.agentService = context.agent;
    registerA2ACallMessageHandlers(context, this.createClientDeps(context), {
      networkAskTimeoutMs: this.config.networkAskTimeoutMs,
    });

    if (this.hasWebserver) {
      context.endpoints.register({
        label: "A2A",
        url: "/a2a",
        priority: 25,
      });
      context.interactions.register({
        id: "a2a",
        label: "A2A",
        description: "Let other agents discover and talk to this brain.",
        href: "/a2a",
        kind: "agent",
        priority: 25,
      });
      this.logger.info("A2A interface registered", {
        domain: context.domain,
      });
    } else {
      this.logger.info("A2A interface registered in tool-only mode", {
        domain: context.domain,
      });
    }
  }

  protected override async onReady(): Promise<void> {
    this.cardReady = true;
  }

  /**
   * Build the Agent Card from the brain's current identity, tools and skills.
   *
   * Built per request: identity and skills can land after startup (a seeded
   * anchor profile, a generated character, derived skills), and the card
   * must not keep serving the defaults it saw at ready.
   */
  async getAgentCard(): Promise<AgentCard | undefined> {
    if (!this.cardReady) return undefined;
    const context = this.getContext();

    return buildAgentCard({
      character: context.identity.get(),
      profile: context.identity.getProfile(),
      version: packageJson.version,
      ...(context.domain ? { domain: context.domain } : {}),
      ...(this.config.organization
        ? { organization: this.config.organization }
        : {}),
      profileKind: context.profileKinds.getResolved(),
      tools: context.tools.listForPermissionLevel("public"),
      skills: await context.publicSkills.list(),
      authEnabled: false,
    });
  }

  /**
   * Resolve caller permission from a verified HTTP signature when present.
   * Unsigned requests remain public.
   */
  private async resolveCaller(
    request: Request,
    body: string,
  ): Promise<{
    permissionLevel: UserPermissionLevel;
    isAnchor: boolean;
    callerDomain: string | null;
  }> {
    const internalUrl = new URL(request.url);
    const externalUrl = new URL(
      `${internalUrl.pathname}${internalUrl.search}`,
      issuerFromRequest(request),
    );
    const verified = await verifyRequest(
      {
        method: request.method,
        url: externalUrl.toString(),
        headers: request.headers,
        body,
      },
      this.jwksResolver,
    );

    if (verified) {
      const grant = await getActiveAuthService()?.getA2APeerTrust(
        verified.domain,
      );
      const permissionLevel =
        grant?.keyFingerprint === verified.keyFingerprint
          ? grant.grantedLevel
          : "public";

      return {
        permissionLevel,
        isAnchor: this.getContext().permissions.isAnchor(
          "a2a",
          verified.domain,
        ),
        callerDomain: verified.domain,
      };
    }

    return { permissionLevel: "public", isAnchor: false, callerDomain: null };
  }

  private withCors(response: Response): Response {
    const headers = new Headers(response.headers);
    for (const [key, value] of Object.entries(A2A_CORS_HEADERS)) {
      headers.set(key, value);
    }

    return new Response(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  }

  private getOrCreateApp(): Hono {
    if (this.app) {
      return this.app;
    }

    const app = new Hono();

    app.get("/.well-known/agent-card.json", async (c) => {
      const agentCard = await this.getAgentCard();
      if (!agentCard) {
        return this.withCors(c.json({ error: "Agent Card not ready" }, 503));
      }
      return this.withCors(c.json(agentCard));
    });

    app.get("/.well-known/agent-directory.json", async (c) => {
      // Built per request: the directory must reflect approvals and
      // archivals live.
      const directory = await buildAgentDirectory(
        this.getContext().entityService,
      );
      return this.withCors(c.json(directory));
    });

    app.get("/a2a", (c) => {
      return this.withCors(
        c.json(
          {
            error: "Use POST with JSON-RPC 2.0 requests.",
            agentCard: "/.well-known/agent-card.json",
          },
          405,
        ),
      );
    });

    app.options("/a2a", () =>
      this.withCors(new Response(null, { status: 204 })),
    );

    app.post("/a2a", async (c) => {
      if (!this.agentService) {
        return this.withCors(
          c.json(
            {
              jsonrpc: "2.0",
              error: { code: -32603, message: "Agent service not ready" },
              id: null,
            },
            503,
          ),
        );
      }

      const bodyText = await c.req.text();
      let caller: {
        permissionLevel: UserPermissionLevel;
        isAnchor: boolean;
        callerDomain: string | null;
      };
      try {
        caller = await this.resolveCaller(c.req.raw, bodyText);
      } catch {
        return this.withCors(c.json({ error: "Invalid HTTP signature" }, 401));
      }

      let body: unknown;
      try {
        body = JSON.parse(bodyText);
      } catch {
        return this.withCors(
          c.json({
            jsonrpc: "2.0",
            error: { code: -32700, message: "Parse error" },
            id: null,
          }),
        );
      }

      const parsed = jsonrpcRequestSchema.safeParse(body);
      if (!parsed.success) {
        return this.withCors(
          c.json({
            jsonrpc: "2.0",
            error: { code: -32600, message: "Invalid request" },
            id: null,
          }),
        );
      }

      if (parsed.data.method === "message/stream") {
        const streamParams = streamParamsSchema.safeParse(
          parsed.data.params ?? {},
        );

        if (!streamParams.success) {
          return this.withCors(
            c.json({
              jsonrpc: "2.0",
              error: {
                code: -32602,
                message: `Invalid params: ${streamParams.error.message}`,
              },
              id: parsed.data.id,
            }),
          );
        }

        const streamResult = handleStreamMessage(
          parsed.data.id,
          streamParams.data.message,
          {
            taskManager: this.taskManager,
            turnSupervisor: this.turnSupervisor,
            agentService: this.agentService,
            callerPermissionLevel: caller.permissionLevel,
            callerIsAnchor: caller.isAnchor,
            callerDomain: caller.callerDomain,
            publicAsks: this.publicAsks(),
          },
        );

        if ("error" in streamResult) {
          return this.withCors(c.json(streamResult));
        }

        const { stream } = streamResult;

        return this.withCors(
          new Response(stream, {
            headers: {
              "Content-Type": "text/event-stream",
              "Cache-Control": "no-cache",
              Connection: "keep-alive",
            },
          }),
        );
      }

      const response = await handleJsonRpc(parsed.data, {
        taskManager: this.taskManager,
        turnSupervisor: this.turnSupervisor,
        agentService: this.agentService,
        callerPermissionLevel: caller.permissionLevel,
        callerIsAnchor: caller.isAnchor,
        callerDomain: caller.callerDomain,
        publicAsks: this.publicAsks(),
      });

      return this.withCors(c.json(response));
    });

    this.app = app;
    return app;
  }

  override getWebRoutes(): WebRouteDefinition[] {
    if (!this.hasWebserver) {
      return [];
    }

    const handleSharedRoute = (request: Request): Promise<Response> =>
      Promise.resolve(this.getOrCreateApp().fetch(request));

    return [
      {
        path: "/.well-known/agent-card.json",
        method: "GET",
        public: true,
        handler: handleSharedRoute,
      },
      {
        path: "/.well-known/agent-directory.json",
        method: "GET",
        public: true,
        handler: handleSharedRoute,
      },
      {
        path: "/a2a",
        method: "GET",
        public: true,
        handler: handleSharedRoute,
      },
      {
        path: "/a2a",
        method: "POST",
        public: true,
        handler: handleSharedRoute,
      },
      {
        path: "/a2a",
        method: "OPTIONS",
        public: true,
        handler: handleSharedRoute,
      },
    ];
  }

  private createRequestSigner(): A2ARequestSigner | undefined {
    const authService = getActiveAuthService();
    if (!authService) return undefined;

    const issuer = authService.getIssuer();
    // Remote peers cannot resolve loopback JWKS, and signature key IDs require HTTPS.
    if (isLoopbackIssuer(issuer) || new URL(issuer).protocol !== "https:") {
      return undefined;
    }

    return async (request): Promise<void> => {
      const signingKey = await authService.getA2ASigningKey();
      await signRequest(request, signingKey.privateJwk, signingKey.keyId);
    };
  }

  private publicAskAllowance: PublicAskAllowance | undefined;

  /** The public callers' daily allowance, kept in this brain's runtime state. */
  private publicAsks(): PublicAskAllowance {
    this.publicAskAllowance ??= new RuntimePublicAskAllowance(
      this.config.publicAsks,
      this.getContext().runtimeState,
    );
    return this.publicAskAllowance;
  }

  private createClientDeps(context: InterfacePluginContext): A2AClientDeps {
    const requestSigner = this.createRequestSigner();
    return {
      ...(requestSigner ? { requestSigner } : {}),
      ...(this.deps.fetch ? { fetch: this.deps.fetch } : {}),
      requestTimeoutMs: this.config.requestTimeoutMs,
      streamIdleTimeoutMs: this.config.streamIdleTimeoutMs,
      maxNetworkAttempts: this.config.maxNetworkAttempts,
      entityService: context.entityService,
    };
  }

  protected override async getTools(): Promise<Tool[]> {
    return [createAgentCallTool(this.createClientDeps(this.getContext()))];
  }

  protected override createDaemon(): Daemon | undefined {
    return {
      start: async (): Promise<void> => {
        if (this.hasWebserver) {
          this.logger.info("A2A mounted on shared webserver host");
        } else {
          this.logger.info("A2A running without webserver routes");
        }
      },
      stop: async (): Promise<void> => {
        await this.turnSupervisor.close();
        this.logger.info("A2A server stopped");
      },
    };
  }
}
